import {
  AlertCircle,
  Bell,
  Bug,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  LogOut,
  MessageCircle,
  Plus,
  Settings,
  Slash,
  Video,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { cn, useSignOut } from "@/utils/misc";
import { useEffect, useRef, useState } from "react";
import { useMutation, usePaginatedQuery } from "convex/react";

import { Route as ApplyPartnershipRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.apply";
import { Button } from "@/components/ui/button";
import { Route as CampaignsRoute } from "@/routes/_app/campaigns/index";
import { Route as CommunityRoute } from "@/routes/_app/_auth/dashboard/_layout.community";
import { Route as CompetitorsRoute } from "@/routes/_app/_auth/dashboard/_layout.competitors";
import { Route as CreatorContractRoute } from "@/routes/_app/_auth/partnerships/$partnershipId";
import { Route as CustomLeaderboardRoute } from "@/routes/_app/_auth/dashboard/_layout.customLeaderboard";
import { DEMO_IDS } from "@/mock/demo";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { Route as GetInspiredRoute } from "@/routes/_app/_auth/dashboard/_layout.getInspired";
import { Id } from "@cvx/_generated/dataModel";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { Route as LeaderboardRoute } from "@/routes/_app/_auth/dashboard/_layout.leaderboard";
import { Logo } from "@/components/ui/logo";
import { MARKETING_EVENTS } from "@/constants/analyticsEvents";
import { Route as OpportunitiesRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.apply";
import { Route as PayoutsRoute } from "@/routes/_app/_auth/dashboard/_layout.payouts";
import { Route as SettingsRoute } from "@/routes/_app/_auth/dashboard/_layout.settings.index";
import { Route as SuperAdminDashboardRoute } from "@/routes/superadmin-dashboard";
import { SupportWidget } from "@/components/Generic/SupportWidget";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { ThemeSwitcher } from "@/components/ui/theme-switcher";
import { User } from "~/types";
import { api } from "@cvx/_generated/api";
import { buttonVariants } from "@/components/ui/button-util";
import { convexQuery } from "@convex-dev/react-query";
import { useAccount } from "@/state/useAccount";
import { useMixpanel } from "@/hooks";
import { useQuery } from "convex/react";
import { useQuery as useReactQuery } from "@tanstack/react-query";

export function Navigation({ user }: { user: User }) {
  const signOut = useSignOut();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const {
    creatorId,
    appId,
    contractId,
    setAppId,
    setContractId,
    setCreatorId,
    demoModeEnabled,
    demoAppName,
    demoAppLogo,
  } = useAccount();
  const isDashboardPath = matchRoute({ to: DashboardRoute.fullPath });
  const isLeaderboardPath = matchRoute({ to: LeaderboardRoute.fullPath });
  const isCustomLeaderboardPath = matchRoute({
    to: CustomLeaderboardRoute.fullPath,
  });
  const isCommunityPath = matchRoute({ to: CommunityRoute.fullPath });
  const isCompetitorsPath = matchRoute({ to: CompetitorsRoute.fullPath });
  const isPayoutsPath = matchRoute({ to: PayoutsRoute.fullPath });
  const isGetInspiredPath = matchRoute({ to: GetInspiredRoute.fullPath });
  const isOpportunitiesPath = matchRoute({ to: OpportunitiesRoute.fullPath });
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const notificationsContentRef = useRef<HTMLDivElement>(null);
  const mixpanel = useMixpanel();

  // Check if we're in demo mode (founder demo only)
  const isDemo =
    demoModeEnabled &&
    (appId as unknown as string) === (DEMO_IDS.appId as unknown as string);

  const {
    results: notifications,
    loadMore: loadMoreNotifications,
    status: notificationsStatus,
  } = usePaginatedQuery(
    api.notifications.getPaginatedNotifications,
    !isDemo
      ? {
          appId: appId,
          creatorId: creatorId,
        }
      : "skip",
    {
      initialNumItems: 10,
    },
  );

  const selectedContract = contractId
    ? user.creatorPartnerships.find((p) => p._id === contractId)
    : null;

  const setNotificationsAsRead = useMutation(
    api.notifications.setNotificationsAsRead,
  );

  // Query to check if there are video projects for this contract
  const videoProjects = useQuery(
    api.creator_internal_videos.getPaginatedCreatorVideoProjects,
    contractId && creatorId ? { contractId, accountType: "creator" } : "skip",
  );

  // Query to check if there are published videos for this contract
  const hasPublishedVideos = useQuery(
    api.videos.hasPublishedVideos,
    contractId ? { contractId } : "skip",
  );

  // Check if creator is in a custom leaderboard and wants to show it on home screen
  const { data: customLeaderboard } = useReactQuery({
    ...convexQuery(api.creators.getCreatorCustomLeaderboard, {
      creatorId: creatorId as Id<"creators">,
    }),
    enabled: !!creatorId && !contractId && !appId,
  });

  const shouldShowCustomLeaderboard =
    creatorId &&
    !contractId &&
    !appId &&
    user.creator?.showLeaderboardOnHomeScreen &&
    customLeaderboard;

  // Mark visible notifications as read when dropdown opens
  useEffect(() => {
    const markNotificationsAsRead = async () => {
      if (isNotificationsOpen && notifications?.length > 0) {
        // Get unread notification IDs
        const unreadNotificationIds = notifications
          .filter((notification) => !notification.isRead)
          .map((notification) => notification._id);

        if (unreadNotificationIds.length > 0) {
          try {
            await setNotificationsAsRead({
              notificationIds: unreadNotificationIds,
            });
          } catch (error) {
            console.error("Failed to mark notifications as read:", error);
          }
        }
      }
    };

    markNotificationsAsRead();
  }, [isNotificationsOpen, notifications, setNotificationsAsRead]);

  // Handle infinite scrolling for notifications
  useEffect(() => {
    const handleScroll = () => {
      const element = notificationsContentRef.current;
      if (!element) return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

      if (isNearBottom && notificationsStatus === "CanLoadMore") {
        loadMoreNotifications(10);
      }
    };

    const element = notificationsContentRef.current;
    if (element && isNotificationsOpen) {
      element.addEventListener("scroll", handleScroll);
      return () => element.removeEventListener("scroll", handleScroll);
    }
  }, [isNotificationsOpen, notificationsStatus, loadMoreNotifications]);

  // Show notification dot when creator exists but has no socials
  //   const showNotificationDot =
  //     creatorId &&
  //     (!user.creatorSocials || Object.keys(user.creatorSocials).length === 0);

  // Get the active account name
  const activeAccountName = isDemo
    ? demoAppName
    : appId
      ? user.appsList.find((app) => app._id === appId)?.name
      : contractId
        ? user.creatorPartnerships.find((p) => p._id === contractId)?.campaign
            .name
        : user.creator && user.creatorPartnerships.length > 0
          ? user.creatorPartnerships.sort(
              (a, b) => b.campaign._creationTime - a.campaign._creationTime,
            )[0]?.campaign.name
          : user.name;

  // Get the active account avatar
  const activeAccountAvatar = isDemo ? (
    <img
      className="h-8 w-8 rounded-full object-cover"
      alt={`${demoAppName} (Demo)`}
      src={demoAppLogo}
      onError={(e) => {
        (e.target as HTMLImageElement).src =
          "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=200&fit=crop&crop=center";
      }}
    />
  ) : appId ? (
    <img
      className="h-8 w-8 rounded-full object-cover"
      alt={user.appsList.find((app) => app._id === appId)?.name}
      src={user.appsList.find((app) => app._id === appId)?.logo}
    />
  ) : (
    <img
      className="h-8 w-8 rounded-full object-cover"
      alt={user.name ?? user.email}
      src={user.avatarUrl}
    />
  );

  const isNewCreator =
    user.creatorPartnerships.length === 0 &&
    creatorId &&
    user.appsList.length === 0;

  // Check if we should hide the opportunities button for specific campaign
  const hideOpportunitiesContractId = localStorage.getItem(
    "hideOpportunitiesForCampaign",
  );
  const shouldHideOpportunities = hideOpportunitiesContractId
    ? user.creatorPartnerships.some(
        (p) => p._id === hideOpportunitiesContractId && p.status === "active",
      )
    : false;

  console.log(shouldHideOpportunities);

  if (!user) {
    return null;
  }

  return (
    <nav
      className={`sticky top-0 z-50 flex w-full flex-col ${isNewCreator ? "" : "border-b border-border"} bg-card px-6`}
    >
      <div className="mx-auto flex w-full max-w-screen-xl items-center justify-between py-3">
        <div className="flex h-10 items-center gap-2">
          <Link
            to={
              !contractId && user.creatorPartnerships.length > 0
                ? DashboardRoute.fullPath
                : !contractId && creatorId
                  ? ApplyPartnershipRoute.fullPath
                  : DashboardRoute.fullPath
            }
            className="flex h-10 items-center gap-1"
          >
            <Logo />
          </Link>
          {(user.creatorPartnerships.length > 0 || user.appsList.length > 0) &&
            // Show dropdown if user has apps (is a founder)
            user.appsList.length > 0 && (
              <>
                <Slash className="h-6 w-6 -rotate-12 stroke-[1.5px] text-primary/10" />
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="gap-2 px-2 data-[state=open]:bg-primary/5"
                    >
                      <div className="flex items-center gap-2">
                        {contractId ? (
                          <img
                            className="h-8 w-8 rounded-full object-cover"
                            alt={selectedContract?.campaign.name}
                            src={selectedContract?.app.logo}
                          />
                        ) : (
                          activeAccountAvatar
                        )}
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-primary/80">
                            {activeAccountName || ""}
                          </p>
                          {contractId && (
                            <span
                              className={cn(
                                "text-xs px-2 py-0.5 rounded-full font-medium",
                                selectedContract?.status === "active"
                                  ? "bg-green-100 text-green-800"
                                  : selectedContract?.status === "completed"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-red-100 text-red-800",
                              )}
                            >
                              {selectedContract?.status}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="flex flex-col items-center justify-center">
                        <ChevronUp className="relative top-[3px] h-[14px] w-[14px] stroke-[1.5px] text-primary/60" />
                        <ChevronDown className="relative bottom-[3px] h-[14px] w-[14px] stroke-[1.5px] text-primary/60" />
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    sideOffset={8}
                    className="min-w-56 bg-card p-2 max-h-[400px] overflow-y-auto"
                  >
                    <DropdownMenuLabel className="flex items-center text-xs font-normal text-primary/60">
                      Accounts
                    </DropdownMenuLabel>
                    {user.creator &&
                      (user.creatorPartnerships.length > 0 ? (
                        user.creatorPartnerships
                          .sort(
                            (a, b) =>
                              b.campaign._creationTime -
                              a.campaign._creationTime,
                          )
                          .map((partnership) => (
                            <DropdownMenuItem
                              key={partnership.campaign._id}
                              className={cn(
                                "h-10 w-full cursor-pointer justify-between rounded-md px-2 hover:bg-secondary",
                                contractId === partnership._id
                                  ? "bg-secondary"
                                  : "",
                              )}
                              onClick={() => {
                                if (user.creator) {
                                  setCreatorId(user.creator._id);
                                  setContractId(partnership._id);
                                  navigate({ to: DashboardRoute.fullPath });
                                }
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <img
                                  className="h-6 w-6 rounded-full object-cover"
                                  alt={partnership.campaign.name}
                                  src={partnership.app.logo}
                                />
                                <p className="text-sm font-medium text-primary/80">
                                  {partnership.campaign.name}
                                </p>
                                <span
                                  className={cn(
                                    "text-xs px-2 py-0.5 rounded-full font-medium",
                                    partnership.status === "active"
                                      ? "bg-green-100 text-green-800"
                                      : partnership.status === "terminated"
                                        ? "bg-red-100 text-red-800"
                                        : partnership.status === "completed"
                                          ? "bg-blue-100 text-blue-800"
                                          : "bg-yellow-100 text-yellow-800",
                                  )}
                                >
                                  {partnership.status}
                                </span>
                              </div>
                            </DropdownMenuItem>
                          ))
                      ) : (
                        <DropdownMenuItem
                          className="h-10 w-full cursor-pointer justify-between rounded-md px-2 hover:bg-secondary"
                          onClick={() => {
                            setCreatorId(user.creator?._id);
                            navigate({ to: OpportunitiesRoute.fullPath });
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <img
                              className="h-6 w-6 rounded-full object-cover"
                              alt={user.name ?? user.email}
                              src={user.avatarUrl}
                            />
                            <p className="text-sm font-medium text-primary/80">
                              No campaigns yet
                            </p>
                          </div>
                        </DropdownMenuItem>
                      ))}

                    {user.appsList.map((app) => (
                      <DropdownMenuItem
                        key={app._id}
                        className={cn(
                          "h-10 w-full cursor-pointer justify-between rounded-md px-2 hover:bg-secondary mt-1",
                          appId === app._id ? "bg-secondary" : "",
                        )}
                        onClick={() => {
                          setAppId(app._id);
                          navigate({ to: DashboardRoute.fullPath });
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <img
                            className="h-6 w-6 rounded-full object-cover"
                            alt={app.name}
                            src={app.logo}
                          />
                          <p className="text-sm font-medium text-primary/80">
                            {app.name}
                          </p>
                        </div>
                      </DropdownMenuItem>
                    ))}

                    {/* Demo campaign entry */}
                    {demoModeEnabled && (
                      <DropdownMenuItem
                        className={cn(
                          "h-10 w-full cursor-pointer justify-between rounded-md px-2 hover:bg-secondary mt-1",
                        )}
                        onClick={() => {
                          setAppId(DEMO_IDS.appId);
                          navigate({ to: DashboardRoute.fullPath });
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <img
                            className="h-6 w-6 rounded-full object-cover"
                            alt={`${demoAppName} (Demo)`}
                            src={demoAppLogo}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=200&fit=crop&crop=center";
                            }}
                          />
                          <p className="text-sm font-medium text-primary/80">
                            {demoAppName}
                          </p>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            Demo
                          </span>
                        </div>
                      </DropdownMenuItem>
                    )}

                    {(user.appsList.length === 0 && !shouldHideOpportunities) ||
                    TEAM_USER_IDS.includes(user._id) ? (
                      <DropdownMenuItem
                        className="mt-2 h-10 w-full cursor-pointer justify-between rounded-md px-2 hover:bg-secondary"
                        onClick={() => {
                          setCreatorId(user.creator?._id);
                          navigate({ to: OpportunitiesRoute.fullPath });
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <Plus className="h-4 w-4 stroke-[1.5px] text-primary/60" />
                          <p className="text-sm font-medium text-primary/80">
                            New Opportunities
                          </p>
                        </div>
                      </DropdownMenuItem>
                    ) : null}

                    {TEAM_USER_IDS.includes(user._id) && (
                      <>
                        <DropdownMenuSeparator className="mx-0 my-2" />
                        <DropdownMenuItem
                          className="h-10 w-full cursor-pointer justify-between rounded-md px-2 hover:bg-secondary"
                          onClick={() => {
                            navigate({ to: SuperAdminDashboardRoute.fullPath });
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-primary/80">
                              SuperAdmin Dashboard
                            </p>
                          </div>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          {/* Show Home button for creators (without apps) when they have a contract selected */}
          {creatorId &&
            !appId &&
            contractId &&
            user.creatorPartnerships.length > 0 && (
              <>
                <Slash className="h-6 w-6 -rotate-12 stroke-[1.5px] text-primary/10" />
                <Button
                  variant="ghost"
                  className="gap-2 px-2"
                  onClick={() => {
                    setContractId(undefined);
                    navigate({ to: DashboardRoute.fullPath });
                  }}
                >
                  <p className="text-sm font-medium text-primary/80">Home</p>
                </Button>
              </>
            )}
        </div>

        <div className="flex h-10 items-center gap-5 ml-auto">
          {creatorId && (
            <div className="flex items-center gap-2">
              <SupportWidget />
              {!user.creator?.isUGCAcceleratorMember && (
                <Button
                  onClick={() => {
                    // Track click event in Mixpanel
                    mixpanel.track(MARKETING_EVENTS.BECOME_CIR, {
                      source: "Navigation Bar",
                      location: window.location.pathname,
                    });
                    window.open(`https://go.sparkle.so/CG-Web`, "_blank");
                  }}
                  variant="default"
                >
                  Become a Creator-in-Residence 🚀
                </Button>
              )}
            </div>
          )}

          {!isNewCreator && (
            <DropdownMenu modal={false} onOpenChange={setIsNotificationsOpen}>
              <DropdownMenuTrigger>
                <Bell className="h-[20px] w-[20px] stroke-[1.5px] text-primary/60" />
                {notifications?.some(
                  (notification: { isRead: boolean }) => !notification.isRead,
                ) && (
                  <div className="relative bottom-[25px] left-[12px]">
                    <div className="absolute flex h-3 w-3 items-center justify-center rounded-full bg-red-600 ring-2 ring-card">
                      <span className="text-[9px] font-medium text-white">
                        {notifications.filter((n) => !n.isRead).length > 10
                          ? "10+"
                          : notifications.filter((n) => !n.isRead).length}
                      </span>
                    </div>
                    <span className="sr-only">New notifications</span>
                  </div>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent
                ref={notificationsContentRef}
                sideOffset={8}
                className="min-w-56 bg-card p-2 mr-4 max-h-96 overflow-y-auto"
              >
                {notifications?.length === 0 ? (
                  <div className="py-3 px-2 text-center">
                    <p className="text-sm text-primary/60">
                      No notifications yet
                    </p>
                    <p className="text-xs text-primary/40">
                      We'll let you know when something happens!
                    </p>
                  </div>
                ) : (
                  <>
                    {notifications?.map((notification) => (
                      <DropdownMenuItem
                        key={notification._id}
                        onClick={() => {
                          if (notification.redirectTo) {
                            window.location.href = notification.redirectTo;
                          }
                        }}
                        className={cn(
                          `flex items-center gap-3 max-w-[300px] ${notification.redirectTo ? "cursor-pointer" : ""}`,
                          !notification.isRead && "bg-blue-500/10",
                        )}
                      >
                        {notification.message.includes("new chat message") ||
                        notification.message.includes("new comment") ? (
                          <MessageCircle className="h-4 w-4 stroke-[1.5px] text-primary/60" />
                        ) : notification.message.includes("rejected") ||
                          notification.message.includes("terminated") ? (
                          <X className="h-4 w-4 stroke-[1.5px] text-red-500/60" />
                        ) : notification.message.includes("a new document") ? (
                          <FileText className="h-4 w-4 stroke-[1.5px] text-primary/60" />
                        ) : notification.message.includes("accepted") ||
                          notification.message.includes("approved") ? (
                          <Check className="h-4 w-4 stroke-[1.5px] text-green-500/60" />
                        ) : notification.message.includes("needs revision") ? (
                          <AlertCircle className="h-4 w-4 stroke-[1.5px] text-yellow-500/60" />
                        ) : notification.message.includes(
                            "submitted for review",
                          ) ? (
                          <Video className="h-4 w-4 stroke-[1.5px] text-yellow-500/60" />
                        ) : (
                          <Bell className="h-4 w-4 stroke-[1.5px] text-primary/60" />
                        )}
                        <div className="flex flex-col gap-2">
                          <p className="text-sm font-medium text-primary/80">
                            {notification.message}
                          </p>
                          <p className="text-xs text-primary/40">
                            {new Date(
                              notification._creationTime,
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                            })}
                          </p>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    {notificationsStatus === "LoadingMore" && (
                      <div className="py-2 px-2 text-center">
                        <Loader2 className="h-4 w-4 animate-spin text-primary/60" />
                      </div>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <img
                  className="min-h-8 min-w-8 rounded-full object-cover"
                  alt={user.name ?? user.email}
                  src={user.avatarUrl}
                  referrerPolicy="no-referrer"
                />
                {/* {showNotificationDot && (
                  <div className="absolute -right-[-8px] -top-[1px]">
                    <span className="absolute h-2 w-2 rounded-full bg-red-600 ring-2 ring-card" />
                    <span className="absolute h-2 w-2 rounded-full bg-red-600 opacity-75" />
                    <span className="sr-only">New notification</span>
                  </div>
                )} */}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={8}
              className="fixed -right-4 min-w-56 bg-card p-2"
            >
              <DropdownMenuItem className="group flex-col items-start focus:bg-transparent">
                <p className="text-sm font-medium text-primary/80 group-hover:text-primary group-focus:text-primary">
                  {user?.name || ""}
                </p>
                <p className="text-sm text-primary/60">{user?.email}</p>
              </DropdownMenuItem>

              {TEAM_USER_IDS.includes(user._id) && (
                <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-purple-600 dark:text-purple-400">
                  Super Admin Mode
                </DropdownMenuLabel>
              )}

              <DropdownMenuItem
                className="group h-9 w-full cursor-pointer justify-between rounded-md px-2 relative"
                onClick={() => navigate({ to: SettingsRoute.fullPath })}
              >
                <span className="text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  Settings{" "}
                  {/* {showNotificationDot && (
                    <span className="ml-2 inline-block rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-medium text-white">
                      Add socials
                    </span>
                  )} */}
                </span>
                <Settings className="h-[18px] w-[18px] stroke-[1.5px] text-primary/60 group-hover:text-primary group-focus:text-primary" />
              </DropdownMenuItem>

              <DropdownMenuItem
                className={cn(
                  "group flex h-9 justify-between rounded-md px-2 hover:bg-transparent",
                )}
              >
                <span className="w-full text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  Theme
                </span>
                <ThemeSwitcher />
              </DropdownMenuItem>

              <DropdownMenuItem
                className={cn(
                  "group flex h-9 justify-between rounded-md px-2 hover:bg-transparent",
                )}
              >
                <span className="w-full text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  Language
                </span>
                <LanguageSwitcher />
              </DropdownMenuItem>

              <DropdownMenuItem
                className="group h-9 w-full cursor-pointer justify-between rounded-md px-2"
                onClick={() => {
                  window.open(
                    "https://alluring-risk-07b.notion.site/20ef2a7c3bd78052914be46e88791688",
                    "_blank",
                  );
                }}
              >
                <span className="text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  Submit Bug
                </span>
                <Bug className="h-[18px] w-[18px] stroke-[1.5px] text-primary/60 group-hover:text-primary group-focus:text-primary" />
              </DropdownMenuItem>

              <DropdownMenuSeparator className="mx-0 my-2" />

              <DropdownMenuItem
                className="group h-9 w-full cursor-pointer justify-between rounded-md px-2"
                onClick={async () => {
                  await signOut();
                }}
              >
                <span className="text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  Log Out
                </span>
                <LogOut className="h-[18px] w-[18px] stroke-[1.5px] text-primary/60 group-hover:text-primary group-focus:text-primary" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {(!contractId && !appId && !creatorId) ||
      (creatorId && user.creatorPartnerships.length === 0) ? null : (
        <div className="mx-auto flex w-full max-w-screen-xl items-center gap-3">
          {shouldShowCustomLeaderboard && (
            <div
              className={cn(
                `flex h-12 items-center border-b-2`,
                isCustomLeaderboardPath
                  ? "border-primary"
                  : "border-transparent",
              )}
            >
              <Link
                to={CustomLeaderboardRoute.fullPath}
                className={cn(
                  `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                )}
              >
                Leaderboard
              </Link>
            </div>
          )}
          <div
            className={cn(
              `flex h-12 items-center border-b-2`,
              isDashboardPath ? "border-primary" : "border-transparent",
            )}
          >
            <Link
              to={DashboardRoute.fullPath}
              className={cn(
                `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
              )}
            >
              Dashboard
            </Link>
          </div>
          {creatorId && user.creatorPartnerships.length > 0 && !contractId && (
            <div
              className={cn(
                `flex h-12 items-center border-b-2`,
                isOpportunitiesPath ? "border-primary" : "border-transparent",
              )}
            >
              <Link
                to={OpportunitiesRoute.fullPath}
                className={cn(
                  `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                )}
              >
                Your Campaigns
              </Link>
            </div>
          )}
          {(user.creator?.isUGCAcceleratorMember ||
            user.creatorPartnerships.some((p) => p.campaign?.type === "CiR")) &&
            !contractId && (
              <div
                className={cn(
                  `flex h-12 items-center border-b-2`,
                  isCommunityPath ? "border-primary" : "border-transparent",
                )}
              >
                <Link
                  to={CommunityRoute.fullPath}
                  className={cn(
                    `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                  )}
                >
                  Community
                </Link>
              </div>
            )}

          {/* {appId && (
            <div
              className={cn(
                `flex h-12 items-center border-b-2`,
                isPartnershipsPath ? "border-primary" : "border-transparent",
              )}
            >
              <Link
                to={PartnershipsRoute.fullPath}
                className={cn(
                  `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                )}
              >
                Creators
              </Link>
            </div>
          )} */}
          {appId && (
            <div
              className={cn(
                `flex h-12 items-center border-b-2`,
                isPayoutsPath ? "border-primary" : "border-transparent",
              )}
            >
              <Link
                to={PayoutsRoute.fullPath}
                className={cn(
                  `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                )}
              >
                Payouts
              </Link>
            </div>
          )}
          {creatorId && contractId && (
            <>
              <div
                className={cn(
                  `flex h-12 items-center border-b-2`,
                  "border-transparent",
                )}
              >
                <Link
                  to={CreatorContractRoute.fullPath}
                  params={{ partnershipId: contractId }}
                  search={{ tab: "videos" }}
                  className={cn(
                    `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80 gap-2`,
                  )}
                >
                  My Videos
                  {videoProjects && videoProjects.length > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-500/30 dark:text-yellow-400">
                      {videoProjects.length}
                    </span>
                  )}
                </Link>
              </div>
              {hasPublishedVideos && (
                <div
                  className={cn(
                    `flex h-12 items-center border-b-2`,
                    isGetInspiredPath ? "border-primary" : "border-transparent",
                  )}
                >
                  <Link
                    to={GetInspiredRoute.fullPath}
                    className={cn(
                      `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                    )}
                  >
                    Inspiration
                  </Link>
                </div>
              )}
              {(selectedContract?.status === "active" ||
                selectedContract?.status === "completed") && (
                <div
                  className={cn(
                    `flex h-12 items-center border-b-2`,
                    isLeaderboardPath ? "border-primary" : "border-transparent",
                  )}
                >
                  <Link
                    to={LeaderboardRoute.fullPath}
                    className={cn(
                      `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                    )}
                  >
                    Leaderboard
                  </Link>
                </div>
              )}
              <div
                className={cn(
                  `flex h-12 items-center border-b-2`,
                  "border-transparent",
                )}
              >
                <Link
                  to={CreatorContractRoute.fullPath}
                  params={{ partnershipId: contractId }}
                  search={{ openChat: true }}
                  className={cn(
                    `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                  )}
                >
                  Contract/Support
                </Link>
              </div>
              <div
                className={cn(
                  `flex h-12 items-center border-b-2`,
                  "border-transparent",
                )}
              >
                <a
                  href={
                    CampaignsRoute.fullPath +
                    "?campaignId=" +
                    selectedContract?.campaign._id
                  }
                  className={cn(
                    `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Creator Brief
                </a>
              </div>
            </>
          )}

          {appId && (
            <div
              className={cn(
                `flex h-12 items-center border-b-2`,
                isCompetitorsPath ? "border-primary" : "border-transparent",
              )}
            >
              <Link
                to={CompetitorsRoute.fullPath}
                className={cn(
                  `${buttonVariants({ variant: "ghost", size: "sm" })} text-primary/80`,
                )}
              >
                Competitors
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
